import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { ChevronRight } from "lucide-react";
import React, { Suspense, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate, useParams } from "react-router";
import { cn } from "src/common/utils";
import { ScopeGate } from "src/components/ScopeGate";
import { TruncatedMarkdown } from "src/components/TruncatedText";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import GithubIcon from "src/components/ui/icons/github";
import { Link } from "src/components/ui/link";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { PageHeader } from "src/components/ui/PageHeader";
import { SimpleDataTable } from "src/components/ui/SimpleDataTable";
import { StatusBadge } from "src/components/ui/StatusBadge";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";

import type { CapabilityDetailQuery } from "./__generated__/CapabilityDetailQuery.graphql";
import type { CapabilityDetailUpdateMutation } from "./__generated__/CapabilityDetailUpdateMutation.graphql";
import { EditCapabilityDialog } from "./EditCapabilityDialog";

const CAPABILITY_TYPE_LABELS: Record<string, string> = {
  GITHUB_ACTIONS_WORKFLOW: "GitHub Actions",
  BUILDKITE_PIPELINE: "Buildkite",
};

const APPROVAL_STYLE_LABELS: Record<string, string> = {
  AUTOMATIC: "Automatic",
  MANUAL: "Requires Approval",
};

const PRODUCT_SURFACE_LABELS: Record<string, string> = {
  CHAT: "Chat",
  DIGEST: "Digest",
  ALERT_RESPONDER: "Alert Agent",
  UNKNOWN: "Unknown",
};

const PRODUCT_SURFACE_COLORS: Record<string, string> = {
  CHAT: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30",
  DIGEST: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/30",
  ALERT_RESPONDER: "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/30",
  UNKNOWN: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-500/10 dark:text-slate-400 dark:border-slate-500/30",
};

const capabilityDetailQuery = graphql`
  query CapabilityDetailQuery($capabilityId: GlobalID!) {
    node(id: $capabilityId) {
      ... on GraphQLCapability {
        id
        name
        description
        capabilityType
        isActive
        approvalStyle
        targetConfig
        parametersSchema
        approvalRate30d
        executions(limit: 50, sortBy: "created_at", sortOrder: "desc") {
          edges {
            node {
              id
              parameters
              result
              status
              approved
              productSurface
              executionTimeMs
              createdAt
              updatedAt
            }
          }
        }
      }
    }
  }
`;

const updateCapabilityMutation = graphql`
  mutation CapabilityDetailUpdateMutation($input: UpdateCapabilityInput!) {
    updateCapability(input: $input) {
      capability {
        id
        name
        isActive
      }
      error
    }
  }
`;

type ExecutionNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<CapabilityDetailQuery["response"]["node"]>["executions"]
    >["edges"][number]
  >["node"]
>;

export function CapabilityDetail() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { capabilityId } = useParams<{ capabilityId: string }>();
  const { canWrite } = usePermissions();
  const canModify = canWrite("capabilities");

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const data = useLazyLoadQuery<CapabilityDetailQuery>(
    capabilityDetailQuery,
    { capabilityId: capabilityId! },
    { fetchPolicy: "network-only", fetchKey: refreshKey }
  );

  const [commitUpdate, isUpdating] = useMutation<CapabilityDetailUpdateMutation>(updateCapabilityMutation);

  const capability = data.node;

  const executions = useMemo(() => {
    const edges = capability?.executions?.edges ?? [];
    return edges.map((edge) => edge.node).filter((node): node is ExecutionNode => Boolean(node));
  }, [capability?.executions?.edges]);

  const handleToggleActive = () => {
    if (!capability || isUpdating) return;

    const currentIsActive = capability.isActive;

    commitUpdate({
      variables: {
        input: {
          id: capability.id,
          isActive: !currentIsActive,
        },
      },
      onCompleted: (response) => {
        if (response.updateCapability.error) {
          toast({
            title: "Error",
            description: response.updateCapability.error,
            variant: "error",
          });
        } else if (response.updateCapability.capability?.name) {
          toast({
            title: `Capability ${!currentIsActive ? "activated" : "deactivated"}`,
            description: `"${response.updateCapability.capability.name}" has been ${!currentIsActive ? "activated" : "deactivated"} successfully.`,
            variant: "success",
          });
          setRefreshKey((prev) => prev + 1);
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
  };

  const handleDeleted = () => {
    navigate("/capabilities");
  };

  const formatTimestamp = React.useCallback((value: unknown) => {
    if (typeof value !== "string") return "Never";

    const createdAtDate = new Date(value);
    const utcDate = new Date(createdAtDate.getTime() + createdAtDate.getTimezoneOffset() * 60000);
    const utcTime = format(utcDate, "MMM d, HH:mm");
    const localTime = format(createdAtDate, "MMM d, HH:mm");

    return (
      <span>
        {utcTime} UTC <span className="text-xs text-muted-foreground">({localTime} Local)</span>
      </span>
    );
  }, []);

  const columns = useMemo<ColumnDef<ExecutionNode>[]>(
    () => [
      {
        id: "calledAt",
        accessorFn: (row) => row.createdAt,
        header: "Called At",
        meta: {
          headerClassName: "whitespace-nowrap",
          cellClassName: "text-sm text-muted-foreground whitespace-nowrap",
          formatCell: (value) => formatTimestamp(value),
        },
      },
      {
        id: "source",
        accessorFn: (row) => row.productSurface,
        header: "Source",
        cell: ({ row }) => {
          const surface = row.original.productSurface;
          if (!surface) return <span className="text-sm text-slate-500">-</span>;
          return (
            <Badge variant="outline" className={cn("text-xs", PRODUCT_SURFACE_COLORS[surface])}>
              {PRODUCT_SURFACE_LABELS[surface]}
            </Badge>
          );
        },
      },
      {
        id: "status",
        accessorFn: (row) => row.status,
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={cn(
              "text-xs",
              row.original.status === "SUCCESS"
                ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30"
                : row.original.status === "FAILED"
                ? "bg-red-100 text-red-700 border-red-300 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30"
                : "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/30"
            )}
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        id: "parameters",
        accessorFn: (row) => JSON.stringify(row.parameters),
        header: "Parameters",
        cell: ({ row }) => (
          <div className="text-xs font-mono text-muted-foreground truncate max-w-md">
            {JSON.stringify(row.original.parameters, null, 0)}
          </div>
        ),
      },
    ],
    [formatTimestamp]
  );

  if (!capability) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Capability not found</p>
          <Button onClick={() => navigate("/capabilities")}>Back to Capabilities</Button>
        </div>
      </div>
    );
  }

  const isActive = capability.isActive;

  const statusBadge = (
    <StatusBadge
      label={isActive ? "ACTIVE" : "INACTIVE"}
      variant={isActive ? "active" : "inactive"}
      readOnly={!canModify}
      disabled={isUpdating}
      onClick={canModify ? handleToggleActive : undefined}
      title={`Click to ${isActive ? "deactivate" : "activate"} capability`}
      showToggleIcon
    />
  );

  return (
    <>
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <div className="p-4 sm:p-6">
          <PageHeader
            title={capability.name ?? "Untitled Capability"}
            breadcrumb={
              <>
                <Link
                  to="/capabilities"
                  className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
                >
                  Capabilities
                </Link>
                <ChevronRight className="mx-2 w-3 h-3" />
                <span className="text-foreground">{capability.name ?? "Untitled"}</span>
              </>
            }
            badges={statusBadge}
            actions={
              <ScopeGate scope="write:capabilities">
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  Edit
                </Button>
              </ScopeGate>
            }
            details={
              <>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="flex items-center gap-1.5">
                    {capability.capabilityType === "GITHUB_ACTIONS_WORKFLOW" && (
                      <GithubIcon className="h-4 w-4 text-muted-foreground" />
                    )}
                    {capability.capabilityType === "BUILDKITE_PIPELINE" && (
                      <BuildkiteIcon className="h-4 w-4 text-muted-foreground" />
                    )}
                    {CAPABILITY_TYPE_LABELS[capability.capabilityType ?? ""] || capability.capabilityType}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Approval Type:</span>{" "}
                  {APPROVAL_STYLE_LABELS[capability.approvalStyle ?? "MANUAL"] || capability.approvalStyle}
                </div>
                <div className="text-sm text-foreground flex items-baseline">
                  <span className="text-muted-foreground shrink-0">Description:&nbsp;</span>
                  {capability.description ? (
                    <TruncatedMarkdown
                      text={capability.description}
                      className="flex-1 min-w-0"
                    />
                  ) : (
                    <span className="text-muted-foreground">No description</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Approval Rate (Last 30 Days):</span>{" "}
                  {executions.length === 0 ? "-" : `${(capability.approvalRate30d ?? 100).toFixed(1)}%`}
                </div>
                <div>
                  <span className="text-muted-foreground">Times Used:</span> {executions.length}
                </div>
              </>
            }
          />

          <div className="mt-6">
            <SimpleDataTable
              data={executions}
              columns={columns}
              enableSorting
              enableCellFormatting
              getRowId={(row) => row.id}
              emptyState={<span className="text-muted-foreground">No executions yet.</span>}
            />
          </div>
        </div>
      </div>

      {/* Edit Capability Dialog */}
      {isEditDialogOpen && (
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner className="h-8 w-8 text-gray-400" />
            </div>
          }
        >
          <EditCapabilityDialog
            capabilityId={capabilityId!}
            isOpen={isEditDialogOpen}
            onClose={handleEditClose}
            onDeleted={handleDeleted}
          />
        </Suspense>
      )}
    </>
  );
}
